import React, { useEffect, useRef, useState, useImperativeHandle, forwardRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import eventBus, { EVENT_NAMES } from '../../../utils/event_bus';
import truckIcon from '../../../static/images/icon_map_truck.png';

// 导入样式和逻辑
import {
  mapContainerStyle,
  mapStyle,
  locationInfoStyle,
  locationInfoContentStyle,
  locationTextStyle,
  closeButtonStyle,
  searchContainerStyle,
  searchInputStyle,
  searchButtonStyle,
  searchResultsStyle,
  searchResultItemStyle,
  toggleButtonStyle
} from './map_view.styles';
import {
  LocationInfo,
  SearchResult,
  MapMarkerInfo,
  MapOriginLocation,
  initializeMap,
  locateCurrentPosition,
  searchAddress,
  showSearchResultOnMap,
  updateMarkers
} from './map_view.logic';

interface MapViewProps {
  center?: [number, number]; // 地图中心点坐标 [纬度, 经度]
  zoom?: number; // 缩放级别
  height?: string | number; // 地图容器高度
  width?: string | number; // 地图容器宽度
  onMapReady?: (map: L.Map) => void; // 地图初始化完成回调
  /** 初始圆心位置（例如货源发货地），地图就绪后可靠绘制标记 */
  originLocation?: MapOriginLocation | null;
  /** 初始搜索半径（公里），与 originLocation 配合绘制范围圆 */
  searchRadiusKm?: number | null;
  /**
   * @deprecated 使用事件总线代替直接回调，请订阅 EVENT_NAMES.MAP_LOCATION_CHANGE 事件
   * 示例: eventBus.subscribe(EVENT_NAMES.MAP_LOCATION_CHANGE, (lat, lng, address) => { ... })
   */
  onLocationChange?: (latitude: number, longitude: number, address: string) => void; // 位置变更回调（已弃用）
}

// 地图组件对外暴露的方法
export interface MapViewRef {
  updateMapMarkers: (markers: MapMarkerInfo[]) => void; // 更新地图标记点
}

/**
 * 基于Leaflet和天地图的地图组件
 * 可以被其他页面引用
 * 
 * 使用示例：
 * ```tsx
 * // 订阅地图位置变化事件
 * useEffect(() => {
 *   const unsubscribe = eventBus.subscribe(EVENT_NAMES.MAP_LOCATION_CHANGE, 
 *     (latitude, longitude, address) => {
 *       console.log('地图位置变化:', latitude, longitude, address);
 *       // 处理位置变化...
 *     }
 *   );
 *   
 *   // 组件卸载时取消订阅
 *   return () => unsubscribe();
 * }, []);
 * ```
 */
const MapView = forwardRef<MapViewRef, MapViewProps>(({
  center = [35.0, 105.0], // 默认中国中心点
  zoom = 5,
  height = '500px',
  width = '100%',
  onMapReady,
  originLocation = null,
  searchRadiusKm = null,
  onLocationChange
}, ref) => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const locationMarkerRef = useRef<L.CircleMarker | null>(null);
  const originLabelMarkerRef = useRef<L.Marker | null>(null);
  const locationCircleRef = useRef<L.Circle | null>(null);
  const distanceCircleRef = useRef<L.Circle | null>(null);
  const markersRef = useRef<Map<string, L.Marker>>(new Map());
  const distanceRef = useRef<number | null>(null);
  const hasFittedSearchRadiusRef = useRef(false);
  const [isMapReady, setIsMapReady] = useState(false);
  const [currentLocation, setCurrentLocation] = useState<LocationInfo | null>(null);
  const [showLocationInfo, setShowLocationInfo] = useState<boolean>(false);
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [showSearchResults, setShowSearchResults] = useState<boolean>(false);
  const [isSearchResultsCollapsed, setIsSearchResultsCollapsed] = useState<boolean>(false);
  const [showDistanceCircle, setShowDistanceCircle] = useState<boolean>(true);
  const [activeDistanceKm, setActiveDistanceKm] = useState<number | null>(
    searchRadiusKm != null && searchRadiusKm > 0 ? searchRadiusKm : null
  );

  /**
   * 创建发货地/圆心文字标注。
   */
  const createOriginLabelIcon = (label: string) =>
    L.divIcon({
      className: 'map-origin-label',
      html: `<div style="
        transform: translate(-50%, -100%);
        margin-top: -14px;
        white-space: nowrap;
        color: #2e7d32;
        font-size: 13px;
        font-weight: 700;
        line-height: 1;
        text-shadow:
          -1px -1px 0 #fff,
          1px -1px 0 #fff,
          -1px 1px 0 #fff,
          1px 1px 0 #fff;
        pointer-events: none;
      ">${label}</div>`,
      iconSize: [0, 0],
      iconAnchor: [0, 0],
    });

  /**
   * 在地图上绘制圆心标记与小范围定位圆。
   */
  const applyLocationMarker = (
    latitude: number,
    longitude: number,
    address: string,
    label = '当前位置'
  ): L.LatLng | null => {
    if (!mapInstanceRef.current) {
      return null;
    }

    const latlng = L.latLng(latitude, longitude);

    if (locationMarkerRef.current) {
      locationMarkerRef.current.remove();
    }
    if (originLabelMarkerRef.current) {
      originLabelMarkerRef.current.remove();
    }
    if (locationCircleRef.current) {
      locationCircleRef.current.remove();
    }

    locationMarkerRef.current = L.circleMarker(latlng, {
      radius: 9,
      fillColor: '#1565c0',
      color: '#fff',
      weight: 3,
      opacity: 1,
      fillOpacity: 1,
    }).addTo(mapInstanceRef.current);

    originLabelMarkerRef.current = L.marker(latlng, {
      icon: createOriginLabelIcon(label),
      interactive: false,
      zIndexOffset: 1000,
    }).addTo(mapInstanceRef.current);

    locationCircleRef.current = L.circle(latlng, {
      radius: 200,
      fillColor: '#1976d2',
      fillOpacity: 0.12,
      color: '#1976d2',
      weight: 1,
    }).addTo(mapInstanceRef.current);

    setCurrentLocation({ latlng, address, label });
    setShowLocationInfo(true);
    return latlng;
  };

  /**
   * 以指定圆心绘制搜索半径圆，并可选择适配视野。
   */
  const applyDistanceCircle = (
    centerLatLng: L.LatLng,
    distanceKm: number,
    fitBounds = false
  ) => {
    distanceRef.current = distanceKm;
    setActiveDistanceKm(distanceKm);

    if (distanceCircleRef.current) {
      distanceCircleRef.current.remove();
      distanceCircleRef.current = null;
    }

    if (
      !showDistanceCircle ||
      !mapInstanceRef.current ||
      !Number.isFinite(distanceKm) ||
      distanceKm <= 0
    ) {
      return;
    }

    distanceCircleRef.current = L.circle(centerLatLng, {
      radius: distanceKm * 1000,
      color: '#d32f2f',
      fillColor: 'rgba(211, 47, 47, 0.12)',
      fillOpacity: 0.12,
      weight: 2,
      dashArray: '6, 8',
    }).addTo(mapInstanceRef.current);

    if (fitBounds) {
      mapInstanceRef.current.fitBounds(distanceCircleRef.current.getBounds(), {
        padding: [40, 40],
      });
      hasFittedSearchRadiusRef.current = true;
    }
  };

  // 获取当前位置并在地图上标记
  const handleLocateMe = () => {
    locateCurrentPosition(
      mapInstanceRef,
      locationMarkerRef,
      locationCircleRef,
      setCurrentLocation,
      setShowLocationInfo,
      onLocationChange
    );
  };
  
  // 处理搜索按钮点击
  const handleSearch = () => {
    searchAddress(searchKeyword, setSearchResults, setIsSearching);
    setShowSearchResults(true);
  };
  
  // 处理搜索框输入变化
  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchKeyword(e.target.value);
    if (!e.target.value) {
      setSearchResults([]);
      setShowSearchResults(false);
    }
  };
  
  // 处理搜索框按键事件，按回车键搜索
  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };
  
  // 处理搜索结果项点击
  const handleSearchResultClick = (result: SearchResult) => {
    showSearchResultOnMap(
      mapInstanceRef,
      locationMarkerRef,
      locationCircleRef,
      result,
      setCurrentLocation,
      setShowLocationInfo,
      onLocationChange
    );
    // 不再隐藏搜索结果列表
  };

  // 初始化地图
  useEffect(() => {
    return initializeMap(
      mapContainerRef,
      mapInstanceRef,
      center,
      zoom,
      (map) => {
        setIsMapReady(true);
        onMapReady?.(map);
      },
      handleLocateMe
    );
  }, []);
  
  // 订阅地图点击位置变化事件
  useEffect(() => {
    const unsubscribe = eventBus.subscribe(EVENT_NAMES.MAP_LOCATION_CHANGE, (latitude: number, longitude: number, address: string) => {
      if (!mapInstanceRef.current) return;

      const latlng = applyLocationMarker(latitude, longitude, address, '当前位置');
      if (!latlng) {
        return;
      }
      
      // 兼容旧的回调方式
      if (onLocationChange) {
        onLocationChange(latitude, longitude, address);
      }

      if (distanceRef.current) {
        applyDistanceCircle(latlng, distanceRef.current, false);
      }
    });
    
    return () => unsubscribe();
  }, [onLocationChange, showDistanceCircle]);
  
  // 订阅距离变化事件，绘制距离圆
  useEffect(() => {
    const unsubscribe = eventBus.subscribe(EVENT_NAMES.DISTANCE_CHANGE, (distance: number | null) => {
      if (distance === null || !Number.isFinite(distance) || distance <= 0) {
        distanceRef.current = null;
        setActiveDistanceKm(null);
        if (distanceCircleRef.current) {
          distanceCircleRef.current.remove();
          distanceCircleRef.current = null;
        }
        return;
      }

      distanceRef.current = distance;

      if (currentLocation) {
        applyDistanceCircle(currentLocation.latlng, distance, true);
      }
    });
    
    return () => unsubscribe();
  }, [currentLocation, showDistanceCircle]);
  
  // 监听 showDistanceCircle / currentLocation，同步距离圆显示
  useEffect(() => {
    if (!currentLocation || !mapInstanceRef.current) {
      return;
    }

    if (distanceRef.current) {
      applyDistanceCircle(currentLocation.latlng, distanceRef.current, false);
    } else if (distanceCircleRef.current) {
      distanceCircleRef.current.remove();
      distanceCircleRef.current = null;
    }
  }, [showDistanceCircle, currentLocation]);

  // 通过 props 可靠落地发货地标记与搜索半径（避免事件总线时序丢失）
  useEffect(() => {
    if (!isMapReady || !originLocation) {
      return;
    }

    const latlng = applyLocationMarker(
      originLocation.lat,
      originLocation.lon,
      originLocation.address,
      originLocation.label || '发货地'
    );

    if (!latlng) {
      return;
    }

    if (searchRadiusKm != null && searchRadiusKm > 0) {
      applyDistanceCircle(latlng, searchRadiusKm, true);
    }
  }, [isMapReady, originLocation, searchRadiusKm, showDistanceCircle]);
  
  // 暴露组件方法给父组件
  useImperativeHandle(ref, () => ({
    // 更新地图标记点
    updateMapMarkers: (markers: MapMarkerInfo[]) => {
      updateMarkers(mapInstanceRef, markersRef, markers);
    }
  }));
  
  // 创建卡车图标
  const createTruckIcon = () => {
    return L.icon({
      iconUrl: truckIcon,
      iconSize: [32, 32],
      iconAnchor: [16, 16],
      popupAnchor: [0, -16]
    });
  };
  
  // 监听司机数据更新事件
  useEffect(() => {
    const unsubscribe = eventBus.subscribe(EVENT_NAMES.DRIVER_DATA_UPDATED, (drivers) => {
      console.log('收到司机数据更新事件:', drivers);
      if (!mapInstanceRef.current || !drivers || !Array.isArray(drivers)) return;
      
      // 将司机数据转换为地图标记点
      const driverMarkers: MapMarkerInfo[] = drivers.map(driver => {
        // 获取司机的经纬度信息
        const lat = (driver as any).lat || (driver.lastPosition?.coordinates?.[1]);
        const lon = (driver as any).lon || (driver.lastPosition?.coordinates?.[0]);
        
        if (!lat || !lon) return null;
        
        // 获取车牌号信息
        let plateInfo = '';
        if (driver.trucks && driver.trucks.length > 0) {
          const truck = driver.trucks[0];
          plateInfo = `车牌: ${truck.plate || '未知'}<br>`;
        }
        
        return {
          latlng: L.latLng(lat, lon),
          title: driver.name || '未知司机',
          content: `${plateInfo}电话: ${driver.phone || '无'}<br>更新时间: ${driver.updatedAt ? new Date(driver.updatedAt).toLocaleString() : '未知'}`,
          type: 'driver',
          id: driver._id
        };
      }).filter(Boolean) as MapMarkerInfo[];
      
      // 更新地图标记点
      if (driverMarkers.length > 0) {
        // 为每个司机标记设置卡车图标
        const map = mapInstanceRef.current;
        const existingMarkers = markersRef.current;
        const updatedMarkerIds = new Set<string>();
        
        // 添加或更新标记点
        driverMarkers.forEach(marker => {
          const markerId = marker.id || `${marker.latlng.lat}-${marker.latlng.lng}-${marker.type}`;
          updatedMarkerIds.add(markerId);
          
          // 如果标记点已存在，则更新其位置和弹出内容
          if (existingMarkers.has(markerId)) {
            const existingMarker = existingMarkers.get(markerId)!;
            existingMarker.setLatLng(marker.latlng);
            existingMarker.setPopupContent(`<div><strong>${marker.title}</strong><p>${marker.content}</p></div>`);
          } else {
            // 创建新的标记点，使用卡车图标
            const newMarker = L.marker(marker.latlng, {
              icon: createTruckIcon()
            }).addTo(map);
            
            // 添加弹出信息
            newMarker.bindPopup(`<div><strong>${marker.title}</strong><p>${marker.content}</p></div>`);
            
            // 将新标记点添加到集合中
            existingMarkers.set(markerId, newMarker);
          }
        });
        
        // 移除不再需要的标记点
        existingMarkers.forEach((marker, id) => {
          if (!updatedMarkerIds.has(id)) {
            marker.remove();
            existingMarkers.delete(id);
          }
        });
        
        // 已有发货地搜索半径时，保持圆心/半径视野，避免被司机点位覆盖
        if (!distanceCircleRef.current && !hasFittedSearchRadiusRef.current) {
          const bounds = L.latLngBounds(driverMarkers.map(m => m.latlng));
          map.fitBounds(bounds, { padding: [50, 50] });
        }
      }
    });
    
    return () => unsubscribe();
  }, []);
  
  // 监听司机选择事件
  useEffect(() => {
    const unsubscribe = eventBus.subscribe(EVENT_NAMES.DRIVER_SELECTED, (driver) => {
      console.log('收到司机选择事件:', driver);
      if (!mapInstanceRef.current || !driver) return;
      
      // 获取司机的经纬度信息
      const lat = (driver as any).lat || (driver.lastPosition?.coordinates?.[1]);
      const lon = (driver as any).lon || (driver.lastPosition?.coordinates?.[0]);
      
      if (!lat || !lon) {
        console.warn('无法获取司机位置信息:', driver);
        return;
      }
      
      const latlng = L.latLng(lat, lon);
      const markerId = driver._id || `${lat}-${lon}-driver`;
      
      // 获取车牌号信息
      let plateInfo = '';
      if (driver.trucks && driver.trucks.length > 0) {
        const truck = driver.trucks[0];
        plateInfo = `车牌: ${truck.plate || '未知'}<br>`;
      }
      
      // 检查标记点是否已存在
      const existingMarkers = markersRef.current;
      
      if (existingMarkers.has(markerId)) {
        // 如果标记点已存在，则聚焦到该标记点并打开弹出窗口
        console.log('标记点已存在:', markerId);
        const marker = existingMarkers.get(markerId)!;
        mapInstanceRef.current.setView(latlng, 15); // 设置地图视图中心点和缩放级别
        marker.openPopup(); // 打开弹出窗口
      } else {
        // 如果标记点不存在，则创建新的标记点
        const newMarker = L.marker(latlng, {
          icon: createTruckIcon()
        }).addTo(mapInstanceRef.current);
        
        // 添加弹出信息
        const content = `<div><strong>${driver.name || '未知司机'}</strong><p>${plateInfo}电话: ${driver.phone || '无'}<br>更新时间: ${driver.updatedAt ? new Date(driver.updatedAt).toLocaleString() : '未知'}</p></div>`;
        newMarker.bindPopup(content).openPopup();
        
        // 将新标记点添加到集合中
        existingMarkers.set(markerId, newMarker);
        
        // 设置地图视图中心点和缩放级别
        mapInstanceRef.current.setView(latlng, 15);
      }
    });
    
    return () => unsubscribe();
  }, []);
  

  return (
    <div style={mapContainerStyle(height, width)}>
      <div 
        ref={mapContainerRef} 
        style={mapStyle} 
      />
      
      {/* 地址搜索框 */}
      <div style={searchContainerStyle}>
        <div style={{ position: 'relative' }}>
          <input
            type="text"
            placeholder="输入地址搜索..."
            value={searchKeyword}
            onChange={handleSearchInputChange}
            onKeyPress={handleSearchKeyPress}
            style={searchInputStyle}
          />
          <button 
            onClick={handleSearch}
            style={searchButtonStyle}
          >
            🔍
          </button>
        </div>
        
        {/* 显示距离圆复选框 */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          padding: '5px 10px', 
          borderTop: '1px solid #eee',
          backgroundColor: '#f9f9f9'
        }}>
          <input
            type="checkbox"
            id="showDistanceCircle"
            checked={showDistanceCircle}
            onChange={(e) => setShowDistanceCircle(e.target.checked)}
            style={{ marginRight: '5px' }}
          />
          <label 
            htmlFor="showDistanceCircle"
            style={{ fontSize: '12px', cursor: 'pointer', color: '#000' }}
          >
            显示搜索范围
            {activeDistanceKm ? `（${activeDistanceKm}km）` : ''}
          </label>
        </div>
        
        {/* 搜索结果列表 */}
        {showSearchResults && searchResults.length > 0 && !isSearchResultsCollapsed && (
          <div style={searchResultsStyle}>
            {searchResults.map((result, index) => (
              <div 
                key={index}
                onClick={() => handleSearchResultClick(result)}
                style={searchResultItemStyle}
              >
                <div><strong>{result.name}</strong></div>
                <div style={{ fontSize: '12px', color: '#666' }}>{result.address}</div>
              </div>
            ))}
          </div>
        )}
        
        {/* 收起/展开按钮 */}
        {showSearchResults && searchResults.length > 0 && (
          <button 
            onClick={() => setIsSearchResultsCollapsed(!isSearchResultsCollapsed)}
            style={toggleButtonStyle}
          >
            {isSearchResultsCollapsed ? '展开搜索结果 ▼' : '收起搜索结果 ▲'}
          </button>
        )}
        
        {/* 搜索中提示 */}
        {isSearching && (
          <div style={{ padding: '10px', textAlign: 'center', fontSize: '14px', color: '#666' }}>
            搜索中...
          </div>
        )}
        
        {/* 无结果提示 */}
        {showSearchResults && !isSearching && searchResults.length === 0 && searchKeyword && (
          <div style={{ padding: '10px', textAlign: 'center', fontSize: '14px', color: '#666' }}>
            未找到相关地址
          </div>
        )}
      </div>
      
      {/* 位置信息弹窗 */}
      {showLocationInfo && currentLocation && (
        <div style={locationInfoStyle}>
          <div style={locationInfoContentStyle}>
            <div style={{ ...locationTextStyle, color: '#fff' }}>
              <div>
                <strong>{currentLocation.label || '当前位置'}：</strong>
                {currentLocation.address || '未知地址'}
              </div>
              <div style={{ marginTop: 4, fontSize: 12, opacity: 0.9 }}>
                圆心：{currentLocation.latlng.lat.toFixed(5)}, {currentLocation.latlng.lng.toFixed(5)}
                {activeDistanceKm
                  ? ` ｜ 搜索半径：${activeDistanceKm} km`
                  : ''}
              </div>
            </div>
            <button 
              onClick={() => setShowLocationInfo(false)}
              style={{ ...closeButtonStyle, color: '#fff' }}
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  );
});

export default MapView;